import { ChangeEvent, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Divider,
  TextField,
} from '@mui/material';

import { SurveyV2 } from '@act/shared/data-transfer-objects';
import NextQuestionSelector from '../NextQuestionSelector';
import CheckpointEditor from '../CheckpointEditor';
import PromptEditor from '../PromptEditor';

interface OptionEntryProps {
  option: SurveyV2.CreateOptionJSONDto;
  questionKey: string;
  questionCount: number;
  isActionSurvey: boolean;
  handleUpdateOption: (option: SurveyV2.CreateOptionJSONDto) => void;
  formErrors?: any; // Form validation errors from React Hook Form
  questionIndex?: number; // Index of this question in the questions array
  optionIndex?: number; // Index of this option in the question's options array
}

const OptionEntry = ({
  option,
  questionKey,
  questionCount,
  isActionSurvey,
  handleUpdateOption,
  formErrors,
  questionIndex,
  optionIndex,
}: OptionEntryProps) => {
  const [hasNextQuestion, setHasNextQuestion] = useState(
    !!option.nextQuestionKey
  );

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleUpdateOption({
      ...option,
      text: e.target.value,
    });
  };

  const handleHasNextQuestionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const shouldHaveNextQuestion = e.target.checked;
    setHasNextQuestion(shouldHaveNextQuestion);

    if (shouldHaveNextQuestion) {
      handleUpdateOption({
        ...option,
        nextQuestionKey: '',
      });
    } else {
      handleUpdateOption({
        ...option,
        nextQuestionKey: undefined,
      });
    }
  };

  const handleNextQuestionChange = (nextQuestionKey: string) => {
    handleUpdateOption({
      ...option,
      nextQuestionKey,
    });
  };

  const handleHasPromptChange = (e: ChangeEvent<HTMLInputElement>) => {
    const hasPrompt = e.target.checked;

    if (hasPrompt) {
      handleUpdateOption({
        ...option,
        prompt: SurveyV2.DEFAULT_PROMPT,
      });
    } else {
      handleUpdateOption({
        ...option,
        prompt: undefined,
      });
    }
  };

  const handlePromptChange = (prompt: SurveyV2.CreatePromptJSONDto) => {
    handleUpdateOption({
      ...option,
      prompt,
    });
  };

  const handleHasCheckpointChange = (e: ChangeEvent<HTMLInputElement>) => {
    const hasCheckpoint = e.target.checked;

    if (hasCheckpoint) {
      handleUpdateOption({
        ...option,
        checkpoint: SurveyV2.DEFAULT_CHECKPOINT,
      });
    } else {
      handleUpdateOption({
        ...option,
        checkpoint: undefined,
      });
    }
  };

  const handleCheckpointChange = (
    checkpoint: SurveyV2.CreateCheckpointJSONDto
  ) => {
    handleUpdateOption({
      ...option,
      checkpoint,
    });
  };

  const handleReportingSettingsChange = (
    e: ChangeEvent<HTMLInputElement>,
    reportingType:
      | 'isAlreadyCompletedOption'
      | 'isCompletedWithReemaOption'
      | 'isRefusalOption'
      | 'isVerificationOption'
  ) => {
    handleUpdateOption({
      ...option,
      [reportingType]: e.target.checked,
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <TextField
        label="Option text"
        value={option.text || ''}
        onChange={handleTextChange}
        fullWidth
      />

      <Box sx={{ marginTop: '16px' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={hasNextQuestion}
              onChange={handleHasNextQuestionChange}
              disabled={questionCount <= 1}
            />
          }
          label="Has next question"
        />
      </Box>
      {hasNextQuestion && (
        <NextQuestionSelector
          questionKey={questionKey}
          nextQuestionKey={option.nextQuestionKey}
          onChange={handleNextQuestionChange}
        />
      )}
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Box sx={{ width: '50%' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!option.prompt || false}
                onChange={handleHasPromptChange}
              />
            }
            label="Has prompt"
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!option.checkpoint || false}
              onChange={handleHasCheckpointChange}
            />
          }
          label="Has checkpoint"
        />
      </Box>
      {option.prompt && (
        <PromptEditor
          prompt={option.prompt}
          onChange={handlePromptChange}
          errors={formErrors}
          fieldPath={
            questionIndex !== undefined && optionIndex !== undefined
              ? `surveyContent.surveyVersion.questions.${questionIndex}.options.${optionIndex}.prompt`
              : undefined
          }
        />
      )}
      {option.checkpoint && (
        <CheckpointEditor
          checkpoint={option.checkpoint}
          onChange={handleCheckpointChange}
        />
      )}
      {isActionSurvey && (
        <Box sx={{ marginBottom: '16px' }}>
          <Divider
            sx={{
              margin: '8px',
              opacity: 1,
              '&::before, &::after': {
                borderTopWidth: '2px',
              },
            }}
          >
            Reporting settings
          </Divider>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Box sx={{ width: '50%' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={option.isAlreadyCompletedOption || false}
                    onChange={e =>
                      handleReportingSettingsChange(
                        e,
                        'isAlreadyCompletedOption'
                      )
                    }
                    disabled={!isActionSurvey}
                  />
                }
                label="Is already completed option"
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={option.isCompletedWithReemaOption || false}
                  onChange={e =>
                    handleReportingSettingsChange(
                      e,
                      'isCompletedWithReemaOption'
                    )
                  }
                  disabled={!isActionSurvey}
                />
              }
              label="Is completed with Reema option"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Box sx={{ width: '50%' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={option.isRefusalOption || false}
                    onChange={e =>
                      handleReportingSettingsChange(e, 'isRefusalOption')
                    }
                    disabled={!isActionSurvey}
                  />
                }
                label="Is refusal option"
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={option.isVerificationOption || false}
                  onChange={e =>
                    handleReportingSettingsChange(e, 'isVerificationOption')
                  }
                  disabled={!isActionSurvey}
                />
              }
              label="Is verification option"
            />
          </Box>
        </Box>
      )}
      <Divider />
    </Box>
  );
};

export default OptionEntry;
